/**
 * ProductBasicContent - 产品基础信息渲染组件
 */

import React from 'react';

export default function ProductBasicContent({ data, mode = 'view', templateType }) {
  if (!data) return null;

  // 标准模板需要的字段（按图片中的顺序）
  const standardFields = [
    { key: 'productName', label: '产品名称' },
    { key: 'ingredients', label: '配料表' },
    { key: 'netContent', label: '净含量/规格' },
    { key: 'standardCode', label: '产品标准代号' }
  ];

  // 其他字段
  const otherFields = [
    { key: 'manufacturer', label: '生产商' },
    { key: 'address', label: '生产地址' },
    { key: 'origin', label: '产地' },
    { key: 'productionDate', label: '生产日期' },
    { key: 'shelfLife', label: '保质期' },
    { key: 'storageConditions', label: '储存条件' },
    { key: 'productionLicense', label: '生产许可证' },
    { key: 'serviceHotline', label: '服务热线' }
  ];

  const isStandardTemplate = templateType === 'standard-food-label';
  const isBlueWhiteTemplate = templateType === 'blue-white-template';
  const ts = data.tableStyle || {};
  
  // 获取标题配置
  const titleConfig = data.titleConfig || {};
  const shouldShowCustomTitle = titleConfig.enabled && (titleConfig.chinese || titleConfig.english);
  
  // 渲染标题
  const renderTitle = () => {
    if (isBlueWhiteTemplate && shouldShowCustomTitle) {
      return (
        <h3
          className="content-section-heading blue-white-title"
          style={{
            backgroundColor: titleConfig.bgColor || '#1e90ff',
            color: titleConfig.textColor || '#ffffff'
          }}
        >
          {titleConfig.english && (
            <div className="blue-white-title-en">{titleConfig.english}</div>
          )}
          <div className="blue-white-title-cn">{titleConfig.chinese || '产品基础信息'}</div>
        </h3>
      );
    }
    
    if (isStandardTemplate || isBlueWhiteTemplate) {
      return <h3 className="content-section-heading">产品基础信息</h3>;
    }
    
    return null;
  };

  // 标准模板 / 蓝白模板 使用表格布局
  if (isStandardTemplate || isBlueWhiteTemplate) {
    // 获取有值的标准字段
    const visibleStandardFields = standardFields.filter(field => data[field.key]);

    // 获取有值的其他字段（修复：让所有填写的字段都能显示）
    const visibleOtherFields = otherFields.filter(field => data[field.key]);

    const table = (
      <div className="label-content-product-basic">
        <table className="info-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {/* 标准字段 */}
            {visibleStandardFields.map(field => (
              <tr key={field.key} className="info-table-row">
                <td className="info-table-label" style={{ background: ts.labelBgColor, color: ts.labelTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{field.label}</td>
                <td className="info-table-value" style={{ color: ts.valueTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{data[field.key]}</td>
              </tr>
            ))}
            {/* 其他字段（生产日期、保质期等） */}
            {visibleOtherFields.map(field => (
              <tr key={field.key} className="info-table-row">
                <td className="info-table-label" style={{ background: ts.labelBgColor, color: ts.labelTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{field.label}</td>
                <td className="info-table-value" style={{ color: ts.valueTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{data[field.key]}</td>
              </tr>
            ))}
            {/* 自定义字段 */}
            {data.customFields && data.customFields.map((custom, idx) => (
              <tr key={`custom-${idx}`} className="info-table-row">
                <td className="info-table-label" style={{ background: ts.labelBgColor, color: ts.labelTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{custom.label}</td>
                <td className="info-table-value" style={{ color: ts.valueTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{custom.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    // 蓝白模板：标题放置在白卡片外，显示在蓝色背景上
    if (isBlueWhiteTemplate) {
      return (
        <>
          {renderTitle()}
          {table}
        </>
      );
    }
    // 标准模板：标题仍在卡片内部
    return (
      <div className="label-content-product-basic">
        {renderTitle()}
        {table.props.children}
      </div>
    );
  }

  // 默认网格布局
  const allFields = [...standardFields, ...otherFields];
  return (
    <div className="label-content-product-basic">
      <h3 className="content-section-heading">产品信息</h3>
      <div className="info-grid">
        {allFields.map(field => 
          data[field.key] && (
            <div key={field.key} className="info-item">
              <span className="info-label">{field.label}：</span>
              <span className="info-value">{data[field.key]}</span>
            </div>
          )
        )}
        {/* 自定义字段 */}
        {data.customFields && data.customFields.map((custom, idx) => (
          <div key={`custom-${idx}`} className="info-item">
            <span className="info-label">{custom.label}：</span>
            <span className="info-value">{custom.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
